import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardHeader,
  CardMedia,
  CircularProgress,
  Container,
  Divider,
  Hidden,
  IconButton,
  styled,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { tooltipClasses } from "@mui/material/Tooltip";
import {
  BCPBookmark,
  BCPCalendarDay,
  BCPClock,
  BCPCopy,
  BCPEllipsis,
  BCPLayerGroup,
  BCPLinkSimple,
  BCPLocationDot,
  BCPPaintbrush,
  BCPShop,
  BCPStar,
  BCPTicket,
  BCPTrash,
  BCPUser,
  BCPUserGroup
} from "../../icons/bcp/BCPIcons";
import PiratePeteIcon from "../../icons/PiratePeteIcon";
import { useEmbeddedExitConfirm } from "../../../hooks/useEmbeddedExitConfirm";
import EmbeddedExitConfirmDialog from "../../modals/EmbeddedExitConfirmDialog";
import { postNotification } from "../../utilities/Notifications";
import ModalLoadingFull from "../../modals/ModalLoadingFull";
import OrganizationCardSmall from "../OrganizationCardSmall";
import UserCardSmall from "../UserCardSmall";
import { useConfirm } from "material-ui-confirm";
import {
  deleteData,
  getData,
  postData
} from "../../utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import { useTranslation } from "react-i18next";
import moment from "moment";

const IS_GW = getEnvironmentVariable("COMPANY_FLAG") === "gw";

export default function EventCardPreview(props) {
  const {
    organize,
    event,
    preview,
    gameSystemDict,
    authContext,
    deleteEvent,
    preventImageFetch
  } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventCard"
  });

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const {
    dialogOpen,
    handleConfirm,
    handleCancel,
    createClickHandler,
    brandName,
    storagePrefix
  } = useEmbeddedExitConfirm();

  const [photo, setPhoto] = useState(null);
  const [toolTips, setTooltips] = useState({
    copy: t("copyEventLink"),
    registration: t("eventRegistration"),
    bookmark:
      event?.userFollowing || event?.following
        ? t("removeBookmark")
        : t("bookmarkEvent"),
    delete: t("deleteEvent") || "Delete Event"
  });
  const [bookmark, setBookmark] = useState(
    event?.userFollowing || event?.following || false
  );
  const [processing, setProcessing] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const confirm = useConfirm();

  const isAdminOrGameSystemAdmin =
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystemId
    ) ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystem?.id
    );

  const isEventOrganiser =
    organize &&
    !!authContext?.authState?.identity?.userId &&
    authContext?.authState?.identity?.userId === event?.ownerId;

  const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      color: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.87)",
      boxShadow: theme.shadows[1],
      fontSize: 11
    }
  }));

  const iconSx = {
    p: 1,
    borderRadius: 1,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
    "&:hover": { backgroundColor: theme.palette.primary.main },
    "&:active": { backgroundColor: theme.palette.primary.main },
    "&.Mui-disabled": {
      background: theme.palette.text.disabled
    }
  };

  useEffect(() => {
    const fetchPhoto = async () => {
      setPhoto(null);
      if (event?.eventImage?.photoUrl) {
        setPhoto({ url: event.eventImage.photoUrl });
      } else if (event?.photoFile?.cloudFrontDomain) {
        setPhoto(event.photoFile);
      } else if (event?.photoFileId && !preventImageFetch) {
        try {
          const apiPhoto = await getData(`/files/${event.photoFileId}`);
          setPhoto(apiPhoto);
        } catch (err) {
          console.error(err);
        }
      }
    };
    if (!IS_GW) fetchPhoto();
  }, [
    event?.photoFileId,
    event?.photoFile,
    event?.eventImage?.photoUrl,
    preventImageFetch
  ]);

  const handleDeleteEvent = async () => {
    try {
      await confirm({
        title: t("deleteEvent") || "Delete Event",
        description:
          t("deleteEventConfirmation") ||
          "Are you sure you want to delete this event? This action cannot be undone.",
        dialogProps: {
          sx: {
            zIndex: 99999
          }
        }
      });
    } catch (err) {
      return;
    }

    try {
      setDeleteLoading(true);
      await deleteData(`/events/${event?.id}`);
      postNotification(
        "success",
        t("eventDeleted") || "Event deleted successfully"
      );
      if (deleteEvent) {
        deleteEvent(event?.id);
      }
    } catch (err) {
      postNotification("error", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const eventName = event?.name || t("eventName") || "Event Name";
  const gameSystemName =
    event?.gameSystem?.name ||
    event?.gameSystemName ||
    gameSystemDict?.[event?.gameSystemId]?.name ||
    "";

  const eventDate = event?.dates?.start
    ? moment(event.dates.start)
    : event?.eventDate
      ? moment(event.eventDate)
      : null;
  const eventEndDate = event?.dates?.end
    ? moment(event.dates.end)
    : event?.eventEndDate
      ? moment(event.eventEndDate)
      : null;

  const getDateDisplay = () => {
    if (!eventDate) return null;
    return eventDate.format("MMMM D");
  };

  const getTimeDisplay = () => {
    if (!eventDate) return null;
    const startTime = eventDate.format("h:mm A");
    const endTime = eventEndDate ? eventEndDate.format("h:mm A") : null;
    if (endTime) {
      return `${startTime} – ${endTime}`;
    }
    return startTime;
  };

  const getLocationDisplay = () => {
    if (event?.isOnlineEvent) return t("onlineEvent") || "Online Event";
    const city = event?.location?.city || event?.city;
    const state = event?.location?.state || event?.state;
    const country = event?.location?.country || event?.country;
    const locationName = event?.location?.name;
    if (city && state && country) return `${city}, ${state} ${country}`;
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    if (locationName) return locationName;
    return null;
  };

  const getPriceDisplay = () => {
    const amount =
      event?.pricingInfo?.amount ??
      (event?.ticketing?.price != null
        ? event.ticketing.price / 100
        : undefined);
    if (amount === null || amount === undefined || amount <= 0) {
      return "";
    }

    const currency = (
      event?.ticketing?.currency ||
      event?.pricingInfo?.currency ||
      "USD"
    ).toUpperCase();
    if (currency === "USD") return `$${amount}`;
    return `${amount} ${currency}`;
  };

  const getPlayerCountDisplay = () => {
    if (event?.playerCounts !== undefined) {
      const current = event.playerCounts?.total ?? 0;
      const max = event?.ticketing?.numTickets ?? event?.numTickets;
      if (max && max > 0) return `${current} / ${max}`;
      if (current > 0) return `${current}`;
      return null;
    }
    if (event?.countString) return event.countString;
    const current = event?.totalPlayers ?? 0;
    const max = event?.numTickets;
    if (max && max > 0) return `${current} / ${max}`;
    if (current > 0) return `${current}`;
    return null;
  };

  const getRoundsDisplay = () => {
    const rounds = event?.status?.numberOfRounds ?? event?.numberOfRounds;
    if (!rounds && rounds !== 0) return null;
    return `${rounds} Round${rounds !== 1 ? "s" : ""}`;
  };

  const hasCircuits = event?.leagues?.length > 0;
  const hobbyCircuits = event?.leagues?.filter((l) => l.hobby) || [];
  const nonHobbyCircuits = event?.leagues?.filter((l) => !l.hobby) || [];

  const ownerHasImage = Boolean(
    event?.gameStore?.profileUrl ||
      event?.gameStore?.profileImageUrl ||
      event?.organization?.profileUrl ||
      event?.organization?.profileImageUrl ||
      event?.owner?.avatarUrl ||
      event?.owner?.profileImageUrl
  );

  const ownerImage =
    event?.gameStore?.profileUrl ||
    event?.gameStore?.profileImageUrl ||
    event?.organization?.profileUrl ||
    event?.organization?.profileImageUrl ||
    event?.owner?.avatarUrl ||
    event?.owner?.profileImageUrl;

  const ownerName =
    event?.gameStore?.name ||
    event?.organization?.name ||
    (event?.owner?.firstName && event?.owner?.lastName
      ? `${event.owner.firstName} ${event.owner.lastName}`.trim()
      : event?.owner?.name) ||
    "";

  const isOrganization = Boolean(
    event?.gameStore?.id || event?.organization?.id
  );

  const inactiveColor = theme.palette.text.disabled;
  const activeColor = theme.palette.text.primary;
  const iconColor = theme.palette.text.secondary;

  const CardWrapper = preview ? Box : CardActionArea;
  const cardWrapperProps = preview
    ? {}
    : {
        href: organize ? `/organize/event/${event?.id}` : `/event/${event?.id}`,
        target: "_blank",
        onClick: createClickHandler(
          organize ? `/organize/event/${event?.id}` : `/event/${event?.id}`,
          "_blank"
        )
      };

  return (
    <>
      <Card
        sx={{
          height: "100%",
          backgroundColor: "background.paper",
          borderRadius: 2,
          overflow: "hidden",
          ...(preview && { maxWidth: 340, width: 340 })
        }}
        onMouseEnter={() => {
          if (isMdUp) {
            setShowButtons(true);
          }
        }}
        onMouseLeave={() => {
          if (isMdUp) {
            setShowButtons(false);
          }
        }}
      >
        {!IS_GW && !preview && (
          <Container
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              p: 1,
              gap: 1,
              mb: -7,
              ml: [0, -2],
              zIndex: 1000
            }}
          >
            <Hidden mdUp>
              <IconButton
                aria-label="more"
                sx={iconSx}
                onClick={() => {
                  setShowButtons(!showButtons);
                }}
              >
                <BCPEllipsis />
              </IconButton>
            </Hidden>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: 1,
                visibility: [showButtons ? "visible" : "hidden"]
              }}
            >
              {organize && (
                <LightTooltip title={t("duplicateEvent") || "Duplicate Event"}>
                  <IconButton
                    aria-label={`Duplicate ${event?.name || "event"}`}
                    sx={iconSx}
                    href={`/organize/event?copyId=${event?.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <BCPCopy />
                  </IconButton>
                </LightTooltip>
              )}

              {(event?.usingOnlineReg || event?.ticketing?.useTicketing) && !(event.ended || event?.status?.ended) && (
                <LightTooltip title={toolTips.registration}>
                  <IconButton
                    aria-label="registration"
                    sx={iconSx}
                    href={`/event/${event?.id}?active_tab=overview&register=true`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <BCPTicket />
                  </IconButton>
                </LightTooltip>
              )}

              <LightTooltip
                title={toolTips.copy}
                PopperProps={{
                  disablePortal: true,
                  popperOptions: {
                    positionFixed: true,
                    modifiers: {
                      preventOverflow: {
                        enabled: true,
                        boundariesElement: "window"
                      }
                    }
                  }
                }}
              >
                <IconButton
                  aria-label="event link"
                  sx={iconSx}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigator.clipboard.writeText(
                      `${window.location.origin}/event/${event?.id}`
                    );
                    setTooltips({
                      ...toolTips,
                      copy: t("copiedLink")
                    });
                    setTimeout(() => {
                      setTooltips({
                        ...toolTips,
                        copy: t("copyEventLink")
                      });
                    }, 2000);
                  }}
                >
                  <BCPLinkSimple />
                </IconButton>
              </LightTooltip>

              {authContext?.authState?.identity && (
                <LightTooltip
                  title={toolTips.bookmark}
                  PopperProps={{
                    disablePortal: true,
                    popperOptions: {
                      positionFixed: true,
                      modifiers: {
                        preventOverflow: {
                          enabled: true,
                          boundariesElement: "window"
                        }
                      }
                    }
                  }}
                >
                  <IconButton
                    aria-label="bookmark"
                    sx={iconSx}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        setProcessing(true);
                        const body = bookmark ? { unfollow: true } : {};

                        await postData(`/events/${event?.id}/follow`, {
                          body: body
                        });

                        setTooltips({
                          ...toolTips,
                          bookmark: bookmark
                            ? t("removedBookmark")
                            : t("bookmarkedEvent")
                        });
                        setTimeout(() => {
                          setTooltips({
                            ...toolTips,
                            bookmark: bookmark
                              ? t("bookmarkEvent")
                              : t("removeBookmark")
                          });
                        }, 2000);

                        setBookmark(!bookmark);
                      } catch (err) {
                        postNotification("error", err);
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    disabled={processing}
                  >
                    {processing ? (
                      <CircularProgress size={"22px"} />
                    ) : (
                      <BCPBookmark outlined={bookmark} />
                    )}
                  </IconButton>
                </LightTooltip>
              )}

              {authContext?.authState?.identity &&
                (isAdminOrGameSystemAdmin || isEventOrganiser) && (
                  <LightTooltip
                    title={toolTips.delete}
                    PopperProps={{
                      disablePortal: true,
                      popperOptions: {
                        positionFixed: true,
                        modifiers: {
                          preventOverflow: {
                            enabled: true,
                            boundariesElement: "window"
                          }
                        }
                      }
                    }}
                  >
                    <IconButton
                      aria-label="delete event"
                      sx={{
                        ...iconSx,
                        color: "error.main",
                        "&:hover": {
                          backgroundColor: "error.main",
                          color: "error.contrastText"
                        },
                        "&:active": {
                          backgroundColor: "error.main",
                          color: "error.contrastText"
                        }
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteEvent();
                      }}
                      disabled={deleteLoading || processing}
                    >
                      {deleteLoading ? (
                        <CircularProgress size={"22px"} color="error" />
                      ) : (
                        <BCPTrash />
                      )}
                    </IconButton>
                  </LightTooltip>
                )}
            </Box>
          </Container>
        )}

        <CardWrapper {...cardWrapperProps} sx={{ height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              justifyContent: "space-between",
              flex: 1
            }}
          >
            {!IS_GW && (
              <Box sx={{ position: "relative" }}>
                <CardMedia
                  component="img"
                  image={
                    photo?.url ||
                    event?.eventImage?.photoUrl ||
                    event?.photoUrl ||
                    `${getEnvironmentVariable(
                      "SYSTEM_IMAGES_CLOUDFRONT"
                    )}/images/EventImage${
                      theme?.palette?.mode === "dark" ? "Dark" : ""
                    }.png`
                  }
                  alt={eventName}
                  sx={{
                    maxHeight: 230,
                    objectFit: "cover",
                    backgroundColor: "#222222",
                    aspectRatio: "16/9"
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 80,
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
                    pointerEvents: "none"
                  }}
                />

                {hasCircuits && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.5
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#fff",
                        fontWeight: 500,
                        mr: 0.5
                      }}
                    >
                      Circuit(s)
                    </Typography>
                    {nonHobbyCircuits.length > 0 && (
                      <Tooltip
                        title={nonHobbyCircuits.map((c) => c.name).join(", ")}
                        arrow
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            cursor: "pointer"
                          }}
                        >
                          <BCPStar size="xs" style={{ color: "#fff" }} />
                        </Box>
                      </Tooltip>
                    )}
                    {hobbyCircuits.length > 0 && (
                      <Tooltip
                        title={hobbyCircuits.map((c) => c.name).join(", ")}
                        arrow
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            cursor: "pointer"
                          }}
                        >
                          <BCPPaintbrush size="xs" style={{ color: "#fff" }} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                )}

                {(event?.flags?.testEvent === true ||
                  event?.testEvent === true) && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 150,
                      height: 150,
                      overflow: "hidden",
                      pointerEvents: "none",
                      zIndex: 2
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 25,
                        right: -35,
                        width: 180,
                        backgroundColor: theme.palette.warning.main,
                        color: theme.palette.warning.contrastText,
                        textAlign: "center",
                        transform: "rotate(45deg)",
                        transformOrigin: "center",
                        py: 0.5,
                        boxShadow: 3
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          letterSpacing: 0.5,
                          textTransform: "uppercase"
                        }}
                      >
                        Test Event
                      </Typography>
                    </Box>
                  </Box>
                )}

                {(ownerName || event?.gameStore?.name || preview) && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      left: 8,
                      right: 8
                    }}
                  >
                    {preview ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5
                        }}
                      >
                        {ownerHasImage ? (
                          <Avatar
                            src={ownerImage}
                            alt={ownerName}
                            sx={{
                              width: 48,
                              height: 48,
                              border: `3px solid ${theme.palette.background.paper}`,
                              boxShadow: 1
                            }}
                          />
                        ) : isOrganization ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              border: `3px solid ${theme.palette.background.paper}`,
                              backgroundColor: "background.secondary",
                              boxShadow: 1
                            }}
                          >
                            <BCPShop size="sm" style={{ color: iconColor }} />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              border: `3px solid ${theme.palette.background.paper}`,
                              backgroundColor: "background.secondary",
                              boxShadow: 1
                            }}
                          >
                            <BCPUser size="sm" style={{ color: iconColor }} />
                          </Box>
                        )}
                        <Typography
                          variant="body1"
                          sx={{
                            color: "#fff",
                            fontWeight: 600,
                            textShadow: "0 1px 3px rgba(0,0,0,0.6)"
                          }}
                        >
                          {ownerName || t("selfNoOrganization")}
                        </Typography>
                      </Box>
                    ) : event?.gameStore?.id || event?.organization?.id ? (
                      <OrganizationCardSmall
                        organization={event.gameStore || event.organization}
                        buttonSx={{
                          backgroundColor: "rgba(0, 0, 0, 0.35)",
                          backdropFilter: "blur(4px)",
                          borderRadius: 2,
                          px: 1,
                          py: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.55)"
                          },
                          "& .MuiTypography-root": {
                            color: "#fff",
                            textShadow: "0 1px 3px rgba(0,0,0,0.6)"
                          }
                        }}
                        avatarSx={{
                          border: `2px solid ${theme.palette.background.paper}`,
                          boxShadow: 1
                        }}
                        organize={organize}
                      />
                    ) : (
                      <UserCardSmall
                        user={event?.owner}
                        buttonSx={{
                          backgroundColor: "rgba(0, 0, 0, 0.35)",
                          backdropFilter: "blur(4px)",
                          borderRadius: 2,
                          px: 1,
                          py: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.55)"
                          },
                          "& .MuiTypography-root": {
                            color: "#fff",
                            textShadow: "0 1px 3px rgba(0,0,0,0.6)"
                          }
                        }}
                        avatarSx={{
                          border: `2px solid ${theme.palette.background.paper}`,
                          boxShadow: 1
                        }}
                        organize={organize}
                      />
                    )}
                  </Box>
                )}
              </Box>
            )}

            {IS_GW && (
              <CardHeader
                title={
                  <>
                    {!getEnvironmentVariable("SINGLE_GAME_SYSTEM") && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: "600",
                          fontSize: "16px",
                          lineHeight: "1.334",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          textTransform: "uppercase"
                        }}
                      >
                        <PiratePeteIcon fontSize="medium" />
                        {event?.gameSystem?.name || event?.gameSystemName || ""}
                      </Typography>
                    )}

                    <Divider
                      sx={{
                        mt: 1,
                        mb: 1,
                        borderColor:
                          event?.gameSystem?.code === 1 || event?.gameType === 1
                            ? "#2b658e"
                            : event?.gameSystem?.code === 4 ||
                                event?.gameType === 4
                              ? "#d3ba6e"
                              : event?.gameSystem?.code === 8 ||
                                  event?.gameType === 8
                                ? "#a50a0e"
                                : event?.gameSystem?.code === 9 ||
                                    event?.gameType === 9
                                  ? "#a67c00"
                                  : event?.gameSystem?.code === 15 ||
                                      event?.gameType === 15
                                    ? "#99bfa4"
                                    : event?.gameSystem?.code === 26 ||
                                        event?.gameType === 26
                                      ? "#2b658e"
                                      : "link.main",
                        borderWidth: "3px",
                        width: "150%",
                        ml: -3
                      }}
                    />

                    <Typography
                      gutterBottom
                      sx={{
                        m: 0,
                        fontWeight: "700",
                        fontSize: "20px",
                        lineHeight: "1.334",
                        wordBreak: "break-word",
                        overflowWrap: "break-word"
                      }}
                    >
                      {eventName}
                    </Typography>
                  </>
                }
              />
            )}

            {/* Card Body */}
            <Box
              sx={{
                p: 2,
                pb: 0
              }}
            >
              {!IS_GW && (
                <>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      lineHeight: 1.3,
                      mb: 0.25,
                      textTransform: "none",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",

                      // Limit to 2 lines with ellipsis
                      maxLines: 2,
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden"
                    }}
                  >
                    {eventName}
                  </Typography>
                  {gameSystemName && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 400,
                        mb: 1.5
                      }}
                    >
                      {gameSystemName}
                    </Typography>
                  )}
                </>
              )}

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <BCPCalendarDay
                    size="sm"
                    style={{
                      color: getDateDisplay() ? iconColor : inactiveColor
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: getDateDisplay() ? activeColor : inactiveColor,
                      fontWeight: 500
                    }}
                  >
                    {getDateDisplay() || "—"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <BCPClock
                    size="sm"
                    style={{
                      color: getTimeDisplay() ? iconColor : inactiveColor
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: getTimeDisplay() ? activeColor : inactiveColor,
                      fontWeight: 500
                    }}
                  >
                    {getTimeDisplay() || "—"}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <BCPLocationDot
                  size="sm"
                  style={{
                    color: getLocationDisplay() ? iconColor : inactiveColor
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: getLocationDisplay() ? activeColor : inactiveColor,
                    fontWeight: 500
                  }}
                >
                  {getLocationDisplay() || "—"}
                </Typography>
              </Box>
            </Box>

            {/* Card Footer */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mx: 2,
                pt: 2,
                pb: 2,
                borderTop: `1px solid ${theme.palette.divider}`
              }}
            >
              <LightTooltip
                title={
                  (event?.usingOnlineReg || event?.ticketing?.useTicketing) && !(event.ended || event?.status?.ended)
                    ? toolTips.registration
                    : ""
                }
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                    borderRadius: 1,
                    py: 0.5,
                    ...((event?.usingOnlineReg || event?.ticketing?.useTicketing) &&
                      !(event.ended || event?.status?.ended) && {
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover
                        }
                      })
                  }}
                  onClick={
                    (event?.usingOnlineReg || event?.ticketing?.useTicketing) && !(event.ended || event?.status?.ended)
                      ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(
                            `/event/${event?.id}?active_tab=overview&register=true`,
                            "_blank"
                          );
                        }
                      : undefined
                  }
                >
                  <BCPTicket
                    size="md"
                    style={{
                      color:
                        getPriceDisplay() ||
                        ((event?.usingOnlineReg || event?.ticketing?.useTicketing) && !(event.ended || event?.status?.ended))
                          ? iconColor
                          : inactiveColor
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        getPriceDisplay() ||
                        ((event?.usingOnlineReg || event?.ticketing?.useTicketing) && !(event.ended || event?.status?.ended))
                          ? activeColor
                          : inactiveColor,
                      fontWeight: 600
                    }}
                  >
                    {getPriceDisplay() || "—"}
                  </Typography>
                </Box>
              </LightTooltip>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5
                }}
              >
                <BCPUserGroup
                  size="md"
                  style={{
                    color: getPlayerCountDisplay() ? iconColor : inactiveColor
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: getPlayerCountDisplay()
                      ? activeColor
                      : inactiveColor,
                    fontWeight: 500
                  }}
                >
                  {getPlayerCountDisplay() || "—"}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5
                }}
              >
                <BCPLayerGroup
                  size="md"
                  style={{
                    color: getRoundsDisplay() ? iconColor : inactiveColor
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: getRoundsDisplay() ? activeColor : inactiveColor,
                    fontWeight: 500
                  }}
                >
                  {getRoundsDisplay() || "—"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardWrapper>
      </Card>

      <EmbeddedExitConfirmDialog
        open={dialogOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        brandName={brandName}
        storagePrefix={storagePrefix}
      />

      {(processing || deleteLoading) && <ModalLoadingFull />}
    </>
  );
}
